/**
 * TASK-008 : HLSPlayer — Wraps hls.js for M3U8 stream playback.
 *
 * Loads via a streamToken-based proxy URL. On token-expired errors
 * the caller can supply an onTokenExpired callback to get a fresh token.
 *
 * @module hls-player
 */

export class HLSPlayer {
  /**
   * TASK-008 : Create a new HLSPlayer.
   *
   * @param {HTMLVideoElement} video
   * @param {object}           options
   * @param {Function}         [options.onError]        - Called with error message string.
   * @param {Function}         [options.onTokenExpired] - Called when proxy returns token_expired; should resolve to new URL.
   */
  constructor( video, options = {} ) {
    this.video    = video;
    this.options  = options;
    this.hls      = null;
    this._overlay = null;
    this._createLoadingOverlay();
  }

  _createLoadingOverlay() {
    const area = this.video ? this.video.closest( '.azleentv-player-area' ) : null;
    if ( ! area ) return;
    let overlay = area.querySelector( '.azleentv-loading-overlay' );
    if ( ! overlay ) {
      overlay = document.createElement( 'div' );
      overlay.className = 'azleentv-loading-overlay';
      overlay.innerHTML = '<div class="azleentv-spinner"></div>';
      area.appendChild( overlay );
    }
    this._overlay = overlay;
  }

  _showLoading() { if ( this._overlay ) this._overlay.classList.add( 'is-visible' ); }
  _hideLoading() { if ( this._overlay ) this._overlay.classList.remove( 'is-visible' ); }

  /**
   * TASK-008 : Build the proxy URL from ajaxUrl + channel id + token.
   *
   * @param {number} channelId
   * @param {string} token
   * @returns {string}
   */
  static proxyUrl( channelId, token ) {
    const base = window.azleentvData ? window.azleentvData.ajaxUrl : '/wp-admin/admin-ajax.php';
    return base + '?action=azleentv_stream&id=' + encodeURIComponent( channelId ) + '&token=' + encodeURIComponent( token );
  }

  /**
   * TASK-008 : Load a stream.
   *
   * @param {string} url - Proxy URL (or direct m3u8 when no encryption key is set).
   */
  load( url ) {
    const video = this.video;
    if ( ! video || ! url ) return;

    this._showLoading();

    // ── Native HLS (Safari / iOS) ──────────────────────────────────────────
    if ( video.canPlayType( 'application/vnd.apple.mpegurl' ) ) {
      video.src = url;
      video.load();
      video.addEventListener( 'canplay', () => this._hideLoading(), { once: true } );
      video.play().catch( () => {} );
      return;
    }

    // ── hls.js ────────────────────────────────────────────────────────────
    if ( typeof Hls === 'undefined' || ! Hls.isSupported() ) {
      this._hideLoading();
      if ( this.options.onError ) this.options.onError( 'Your browser does not support HLS streams.' );
      return;
    }

    this.hls = new Hls( {
      maxBufferLength:   30,
      maxMaxBufferLength: 120,
      enableWorker:      true,
      lowLatencyMode:    true,
      backBufferLength:  60,
      startFragPrefetch: true,
      xhrSetup: ( xhr ) => {
        // TASK-008 : Allow credentials for same-origin proxy requests.
        xhr.withCredentials = true;
      },
    } );

    this.hls.loadSource( url );
    this.hls.attachMedia( video );

    this.hls.on( Hls.Events.MANIFEST_PARSED, () => {
      video.play().catch( () => {} );
    } );

    video.addEventListener( 'canplay', () => this._hideLoading(), { once: true } );

    this.hls.on( Hls.Events.ERROR, ( _e, data ) => {
      if ( data.fatal ) {
        this._hideLoading();
        // TASK-008 : Check for token_expired response from our proxy.
        if ( data.type === Hls.ErrorTypes.NETWORK_ERROR && data.response ) {
          try {
            const body = JSON.parse( data.response.data );
            if ( body && body.data && body.data.code === 'token_expired' && this.options.onTokenExpired ) {
              this.options.onTokenExpired();
              return;
            }
          } catch ( _err ) { /* not JSON */ }
        }

        switch ( data.type ) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            this.hls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            this.hls.recoverMediaError();
            break;
          default:
            if ( this.options.onError ) this.options.onError( 'Stream error. Please try another channel.' );
            this.destroy();
        }
      }
    } );
  }

  destroy() {
    if ( this.hls ) { this.hls.destroy(); this.hls = null; }
    if ( this.video ) {
      this.video.pause();
      this.video.removeAttribute( 'src' );
      this.video.load();
    }
    this._hideLoading();
  }
}
